import { createContext, useContext, useState, useEffect } from 'react';
import { useUserPreferences } from './UserPreferencesContext';

const ThemeContext = createContext();

// localStorage is used only as a boot-time cache so the page doesn't flash
// light-then-dark before server preferences arrive. The server's
// preferences.theme is the canonical store.
const THEME_CACHE_KEY = 'theme';
const FONT_SIZE_KEY = 'compact-font';

function getInitialTheme() {
  const stored = localStorage.getItem(THEME_CACHE_KEY);
  if (stored === 'dark' || stored === 'light') return stored;
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

export function ThemeProvider({ children }) {
  const { preferences, loaded, savePreferences } = useUserPreferences();
  const [theme, setTheme] = useState(getInitialTheme);
  const [compactFont, setCompactFont] = useState(() => localStorage.getItem(FONT_SIZE_KEY) === 'true');

  // Adopt server theme once preferences load.
  useEffect(() => {
    if (loaded && preferences.theme && preferences.theme !== theme) {
      setTheme(preferences.theme);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, preferences.theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    localStorage.setItem(THEME_CACHE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.style.fontSize = compactFont ? '14px' : '';
    localStorage.setItem(FONT_SIZE_KEY, compactFont);
  }, [compactFont]);

  const toggleTheme = async () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    if (loaded) {
      await savePreferences({ ...preferences, theme: next });
    }
  };

  const toggleCompactFont = () => {
    setCompactFont((prev) => !prev);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, compactFont, toggleCompactFont }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
